import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { getRequest, tabulatorGet } from '../../api/ApiController';
import { ReactTabulator, reactFormatter } from 'react-tabulator';
import { Dialog } from 'primereact/dialog';
import { PanelMenu } from 'primereact/panelmenu';
import { OverflowMenuProvider } from '../../components/toolbar/OverflowMenu';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import '../../assets/css/style.css';
import '../../assets/css/iconsmenu.css';
import { EditIcon, PrintIcon, RefreshIcon, SearchIcon, ExportIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, EditRowIcon } from "../svg/svgIcons";
import { MdSearch, MdSearchOff } from "react-icons/md";
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { MaintenanceMaskDialogCheckbox } from '../../components/maintenance/MaintenanceMaskDialogCheckbox';
import IntersectionObserverWrapper from '../../components/toolbar/IntersectionObserverWrap';
import { buildNavigationContextFromTable } from '../../utils/maintenanceNavigationContext';
import { useTabulatorScrollArrows } from '../../utils/useTabulatorScrollArrows';
import { primeMultiSelectHeaderFilter } from '../../utils/primeMultiSelectHeaderFilter';
import { getEffectiveMask } from '../../utils/effectiveMask';
import {
  buildMaintenanceMaskAkcieSubItems,
  createConfirmSaveStandardMask,
  createConfirmApplyStandardLayoutToUserMask,
  createSaveUserMaskColumns,
  createOpenMaskDialog,
  createMaskSetFieldHandler,
  createMaskSelectAllHandler,
  createMaskDeselectAllHandler,
  hasApplicableStandardMaskRow,
  MAINTENANCE_MASK_DIALOG_SKIP_FIELDS,
} from '../../utils/maintenanceTabulatorMaskActions';
import {
  cleanSorters,
  extractSafePaginationParams,
  createDedupedAjaxRequest,
  refreshProgressiveFromStart,
  safeParse,
  applyUserMaskRowToTabulator,
  mergePagedStandardListFiltersAndSorters,
  appendMaintenanceMachineListPagedFilter,
  sanitizeTabulatorLayoutAttributes,
  TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS,
} from '../../utils/tabulatorProgressiveLoading';
import { getTabulatorPersistLayoutEvents } from '../../utils/tabulatorPersistLayout';
import { getPusherChannel } from '../../utils/pusherChannel';
import { inputOnEnterHeaderFilter } from '../../utils/inputOnEnterHeaderFilter';
import { tabulatorColumnMinWidthFromTitle } from '../../utils/tabulatorColumnMinWidth';
import { buildTabulatorIdColumn } from '../../utils/tabulatorIdColumn';
import { buildAuditTabulatorColumns, mergeColumnVisibilityWithAuditDefaults } from '../../utils/tabulatorAuditColumns';
import { LocaleContext } from '../../components/LocaleContext';
import { defaultProductLogoSrc } from '../../constants/brandingDisplay';
import { applyTabulatorRuntimeI18n } from '../../utils/tabulatorRuntimeI18n';
import {
  buildCanonicalPrintFooterHtml,
  buildCanonicalPrintHeaderHtml,
} from '../../utils/printChrome';
const MAINTENANCE_MACHINE_LIST_COLUMN_I18N_KEYS = Object.freeze({
  'name': 'Common.Name',
  'type': 'Common.TaskType',
  'norm': 'Common.Norm',
  'machine_type.name': 'MachineList.Column.MachineTypeName',
  'interval_months': 'Common.IntervalMonths',
  'responsible.name': 'Common.ResponsibleShort',
  'description': 'Common.Description',
  'rowSelection': 'Common.Name',
});



const MASK_PAGE = 'MaintenanceMachineList';

const DEFAULT_MAINTENANCE_MACHINE_LIST_COLUMN_VISIBILITY = Object.freeze({
  id: true,
  name: true,
  type: true,
  norm: true,
  'machine_type.name': true,
  interval_months: true,
  'responsible.name': true,
  description: true,
  created_at: true,
  updated_at: true,
});

function mergeMaintenanceMachineListColumnVisibilityFromSaved(saved) {
  return mergeColumnVisibilityWithAuditDefaults(DEFAULT_MAINTENANCE_MACHINE_LIST_COLUMN_VISIBILITY, saved);
}

function applyMaintenanceMachineListDataColumnVisibility(table, visibility) {
  if (!table) return;
  for (const field of Object.keys(DEFAULT_MAINTENANCE_MACHINE_LIST_COLUMN_VISIBILITY)) {
    if (visibility[field] === false) table.hideColumn?.(field);
    else table.showColumn?.(field);
  }
}

export default function MaintenanceMachineList() {
  const { t, locale } = useContext(LocaleContext);
  const [state, dispatch] = useContext(DatabaseContext);
  const { user } = useContext(AuthContext);
  const {
    openFavoriteDialog,
    toast,
    setFastView,
    setUserMaskFilter,
    setStandardMaskFilter,
    openChosenNotificationPreferences,
    getDateFormat,
    getUserColor,
    getUserInitials,
  } = useContext(UtilContext);

  const [editIcon, setEditIcon] = useState(true);
  const [favoriteIcon] = useState(false);
  const [searchIconColor, setSearchIconColor] = useState('');
  const [visibleSearch, setVisibleSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const submittedSearchValueRef = useRef('');

  const [columnsVisible, setColumnsVisible] = useState(() => ({ ...DEFAULT_MAINTENANCE_MACHINE_LIST_COLUMN_VISIBILITY }));
  const [maskVisible, setMaskVisible] = useState(false);

  const ref = useRef(null);
  const elementRef = useRef(null);
  const overlayRef = useRef(null);
  const inFlightRef = useRef({ sig: null, promise: null });
  const completedCacheRef = useRef(new Map());
  const tableBuiltRef = useRef(false);
  const isRestoringRef = useRef(false);
  const savedHScrollRef = useRef({ left: 0, pending: false });

  const pageSizePc = useMemo(() => {
    const v = (state.module_settings || []).find((s) => s.module === 'global' && s.key === 'page_size_pc')?.value;
    const n = parseInt(v, 10);
    return (Number.isFinite(n) && n > 0) ? n : 50;
  }, [state.module_settings]);

  const dateFormatGlob = getDateFormat?.(state?.module_settings) ?? 'dd.MM.yyyy';
  const dateTimeFormatGlob = `${dateFormatGlob} HH:mm`;
  const canSetStandardMask = user?.permissions?.includes('set standard mask');

  const getTable = useCallback(() => ref.current?.table ?? ref.current, []);
  const refreshFromStart = useCallback(
    () => refreshProgressiveFromStart(getTable, completedCacheRef, t('Common.LoadingEllipsis')),
    [getTable]
  );

  const captureHScroll = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    if (!holder) return;
    savedHScrollRef.current = { left: holder.scrollLeft, pending: true };
  }, [getTable]);

  const restoreHScroll = useCallback(() => {
    if (!savedHScrollRef.current.pending) return;
    const table = getTable();
    if (!table) return;
    const holder = table.rowManager?.element;
    const header = table.columnManager?.contentsElement;
    if (!holder) return;
    const left = savedHScrollRef.current.left;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        holder.scrollLeft = left;
        if (header) header.scrollLeft = left;
        savedHScrollRef.current.pending = false;
      });
    });
  }, [getTable]);

  useEffect(() => {
    const channel = getPusherChannel();
    if (!channel) return;
    const DEDUPE_MS = 800;
    const lastHandled = { key: '', at: 0 };
    const shouldSkip = (eventType, id) => {
      if (id == null) return true;
      const key = `${eventType}-${id}`;
      const now = Date.now();
      if (lastHandled.key === key && now - lastHandled.at < DEDUPE_MS) return true;
      lastHandled.key = key;
      lastHandled.at = now;
      return false;
    };
    const applyWhenIdle = (fn) => {
      const p = inFlightRef.current?.promise;
      if (p) p.finally(() => { try { fn(); } catch (_) {} });
      else { try { fn(); } catch (_) {} }
    };
    const onCreated = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('maintenance_type.created', id)) return;
      getRequest(`/api/maintenance_types/${id}`).then((res) => {
        const rowData = res?.data ?? res;
        if (!rowData?.id) return;
        applyWhenIdle(() => {
          const table = getTable();
          const row = table?.getRow?.(rowData.id);
          if (row) row.update(rowData);
          else table?.addData?.([rowData], true);
        });
      }).catch(() => {});
    };
    const onUpdated = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('maintenance_type.updated', id)) return;
      getRequest(`/api/maintenance_types/${id}`).then((res) => {
        const rowData = res?.data ?? res;
        if (!rowData?.id) return;
        applyWhenIdle(() => {
          const table = getTable();
          const row = table?.getRow?.(rowData.id);
          if (row) row.update(rowData);
          else table?.addData?.([rowData], true);
        });
      }).catch(() => {});
    };
    const onDeleted = (data) => {
      const id = data?.id ?? null;
      if (id == null || shouldSkip('maintenance_type.deleted', id)) return;
      applyWhenIdle(() => {
        const table = getTable();
        const row = table?.getRow?.(id) ?? table?.getRows?.().find((r) => r.getData().id === id);
        if (row) row.delete();
      });
    };
    channel.bind('maintenance_type.created', onCreated);
    channel.bind('maintenance_type.updated', onUpdated);
    channel.bind('maintenance_type.deleted', onDeleted);
    return () => {
      channel.unbind('maintenance_type.created', onCreated);
      channel.unbind('maintenance_type.updated', onUpdated);
      channel.unbind('maintenance_type.deleted', onDeleted);
    };
  }, [getTable]);

  const buildRequestParams = useCallback(
    (paramsFromTabulator = {}) => {
      const extra = { ...extractSafePaginationParams(paramsFromTabulator) };
      if (extra.size == null) extra.size = pageSizePc;
      mergePagedStandardListFiltersAndSorters(extra, {
        getTable,
        tableBuiltForParams: tableBuiltRef.current,
        userMaskFilters: state.user_mask_filters,
        maskPage: 'MaintenanceMachineList',
        appendFilter: appendMaintenanceMachineListPagedFilter,
      });
      const normalizedSearch = String(submittedSearchValueRef.current ?? '').trim();
      if (normalizedSearch !== '') extra.toolbar_search = normalizedSearch;
      return extra;
    },
    [getTable, state.user_mask_filters, pageSizePc]
  );

  const normalizeText = (value) =>
    (value ?? '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  function textHeaderFilter(headerValue, rowValue) {
    if (!headerValue) return true;
    return normalizeText(rowValue).includes(normalizeText(headerValue));
  }

  function onEditRow(maintenance_type) {
    const navigationContext = buildNavigationContextFromTable({
      table: getTable(),
      cleanSorters,
      toolbarSearch: submittedSearchValueRef.current ?? '',
    });
    dispatch({
      type: 'create_new_tab', name: t('MaintenanceMachineList.Text.Zoznam_podla_planovanych_ukonov'), translationKey: 'MaintenanceMachineList.Text.Zoznam_podla_planovanych_ukonov', legacyLabel: t('MaintenanceMachineList.Text.Zoznam_podla_planovanych_ukonov'),
      page: 'MaintenanceMachineDetail',
      props: { maintenance_type, type: 'edit', navigationContext },
      mask: 'mntn022',
    });
  }

  function EditRowFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return (
      <div className="flex items-center justify-center w-full h-full">
        <button type="button" onClick={() => onEditRow(rowData)}>
          <EditRowIcon />
        </button>
      </div>
    );
  }

  function TruncateSpan({ text }) {
    const t = (text ?? '').toString();
    return <span className="block w-full min-w-0 truncate whitespace-nowrap" title={t || undefined}>{t}</span>;
  }

  function TruncateCellFormatter(props) {
    return <TruncateSpan text={props.cell.getValue()} />;
  }

  function ResponsibleFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return <TruncateSpan text={rowData?.responsible?.name ?? ''} />;
  }

  function MachineTypeFormatter(props) {
    const rowData = props.cell.getRow().getData();
    return <TruncateSpan text={rowData?.machine_type?.name ?? ''} />;
  }

  const auditTailColumns = useMemo(
    () =>
      buildAuditTabulatorColumns({
        includeId: false,
        dateFormat: dateFormatGlob,
        dateTimeFormat: dateTimeFormatGlob,
        getUserColor,
        getUserInitials,
        textHeaderFilter,
        remotePassthrough: true,
      }),
    [dateFormatGlob, dateTimeFormatGlob, getUserColor, getUserInitials, textHeaderFilter]
  );

  const machineTypeFilterOptions = useMemo(
    () => (state.machine_types || []).map((mt) => ({ label: mt.name, value: mt.name })),
    [state.machine_types]
  );

  const columns = useMemo(
    () => [
      {
        title: '',
        field: 'rowSelection',
        formatter: 'rowSelection',
        titleFormatter: 'rowSelection',
        hozAlign: 'center',
        width: 44,
        headerSort: false,
        resizable: false,
        frozen: true,
        print: false,
        download: false,
      },
      {
        title: '',
        field: 'editor',
        hozAlign: 'center',
        width: 44,
        minWidth: 44,
        headerSort: false,
        formatter: reactFormatter(<EditRowFormatter />),
        resizable: false,
        frozen: true,
        print: false,
        download: false,
      },
      buildTabulatorIdColumn({
        frozen: true,
        headerFilterFunc: textHeaderFilter,
      }),
      {
        title: t('Common.Name'),
        field: 'name',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Name')),
        widthGrow: 1,
        hozAlign: 'left',
        formatter: reactFormatter(<TruncateCellFormatter />),
      },
      {
        title: t('Common.TaskType'),
        field: 'type',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.TaskType')),
        widthGrow: 1,
        hozAlign: 'left',
        formatter: reactFormatter(<TruncateCellFormatter />),
      },
      {
        title: t('Common.Norm'),
        field: 'norm',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Norm')),
        widthGrow: 1,
        hozAlign: 'left',
        formatter: reactFormatter(<TruncateCellFormatter />),
      },
      {
        title: t('MachineList.Column.MachineTypeName'),
        field: 'machine_type.name',
        minWidth: tabulatorColumnMinWidthFromTitle(t('MachineList.Column.MachineTypeName'), 'multiselect'),
        widthGrow: 1,
        hozAlign: 'left',
        formatter: reactFormatter(<MachineTypeFormatter />),
        headerFilter: primeMultiSelectHeaderFilter,
        headerFilterParams: { placeholder: t('Common.All'), valueType: 'string', options: machineTypeFilterOptions },
        headerFilterFunc: (headerValue, rowValue) => {
          if (!headerValue) return true;
          const selected = new Set(String(headerValue).split(',').map((x) => x.trim()).filter(Boolean));
          return selected.has(String(rowValue ?? ''));
        },
      },
      {
        title: t('Common.IntervalMonths'),
        field: 'interval_months',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.IntervalMonths')),
        widthGrow: 1,
        hozAlign: 'left',
        sorter: 'number',
        formatter: reactFormatter(<TruncateCellFormatter />),
      },
      {
        title: t('Common.ResponsibleShort'),
        field: 'responsible.name',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.ResponsibleShort')),
        widthGrow: 1,
        hozAlign: 'left',
        formatter: reactFormatter(<ResponsibleFormatter />),
      },
      {
        title: t('Common.Description'),
        field: 'description',
        headerFilter: inputOnEnterHeaderFilter,
        headerFilterLiveFilter: false,
        headerFilterFunc: textHeaderFilter,
        minWidth: tabulatorColumnMinWidthFromTitle(t('Common.Description')),
        widthGrow: 1,
        hozAlign: 'left',
        formatter: reactFormatter(<TruncateCellFormatter />),
      },
      ...auditTailColumns,
    ],
    [machineTypeFilterOptions, auditTailColumns, textHeaderFilter]
  );

  const { scrollToFirst, scrollUp, scrollDown, scrollToLast } = useTabulatorScrollArrows(getTable);

  function searchTable() {
    const normalized = String(searchValue ?? '').trim();
    submittedSearchValueRef.current = normalized;
    setSearchIconColor(normalized === '' ? '' : 'active');
    captureHScroll();
    refreshFromStart();
    setVisibleSearch(false);
  }

  function editMaintenanceType() {
    const sel = getTable()?.getSelectedData?.()?.[0];
    if (sel) onEditRow(sel);
  }

  const onSelectRow = (data) => {
    const count = Array.isArray(data) ? data.length : 0;
    const canEdit = user?.permissions?.includes('edit maintenance');
    setEditIcon(!canEdit || count !== 1);
  };

  const itemRenderer = (item, options) => (
    <a className="flex align-items-center px-3 py-2 cursor-pointer items-center bg-[#333] text-white w-[180px]" onClick={options.onClick}>
      <span className={`${item.icon} text-primary`} />
      <span className={`mx-2 ${item.items && 'font-semibold'}`}>{item.label}</span>
    </a>
  );

  const [expandedKeys, setExpandedKeys] = useState({});
  const closeMenu = useCallback(() => {
    setTimeout(() => {
      OverflowMenuProvider.setVisible?.(null);
      setExpandedKeys({});
    }, 0);
  }, []);
  useEffect(() => {
    const table = typeof getTable === 'function' ? getTable() : null;
    if (!table?.getColumns) return;
    applyTabulatorRuntimeI18n(table, t, {
      columnTitleKeys: MAINTENANCE_MACHINE_LIST_COLUMN_I18N_KEYS,
      placeholder: typeof t === 'function' ? t('Common.NoData') : undefined,
      reformatRows: true,
    });
  }, [locale, t, getTable]);


  const hasApplicableStandardMask = useMemo(
    () => hasApplicableStandardMaskRow(state.standard_mask_filters, MASK_PAGE, mergeMaintenanceMachineListColumnVisibilityFromSaved),
    [state.standard_mask_filters]
  );

  const openMaskDialog = useCallback(
    createOpenMaskDialog({
      columnsVisible,
      setColumnsVisible,
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_LIST_COLUMN_VISIBILITY,
      mergeVisibilityFromSaved: mergeMaintenanceMachineListColumnVisibilityFromSaved,
      setMaskVisible,
      closeMenu,
    }),
    [columnsVisible, closeMenu]
  );

  const saveUserMaskColumns = useCallback(
    createSaveUserMaskColumns({
      maskPage: MASK_PAGE,
      getTable,
      setUserMaskFilter,
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_LIST_COLUMN_VISIBILITY,
      columnsVisible,
      setColumnsVisible,
      setMaskVisible,
      toast,
    }),
    [columnsVisible, getTable, setUserMaskFilter, toast]
  );

  const confirmSaveStandardMask = useCallback(
    createConfirmSaveStandardMask({
      maskPage: MASK_PAGE,
      getTable,
      setStandardMaskFilter,
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_LIST_COLUMN_VISIBILITY,
      toast,
    }),
    [getTable, setStandardMaskFilter, toast]
  );

  const confirmApplyStandardLayoutToUserMask = useCallback(
    createConfirmApplyStandardLayoutToUserMask({
      maskPage: MASK_PAGE,
      state,
      setUserMaskFilter,
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_LIST_COLUMN_VISIBILITY,
      mergeVisibilityFromSaved: mergeMaintenanceMachineListColumnVisibilityFromSaved,
      toast,
      closeMenu,
      onAppliedToTable: ({ colsForSave, attributes }) => {
        const table = getTable();
        if (!table) return;
        isRestoringRef.current = true;
        try {
          table.setColumnLayout?.(sanitizeTabulatorLayoutAttributes(attributes, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS));
        } catch (_) {}
        setColumnsVisible(colsForSave);
        applyMaintenanceMachineListDataColumnVisibility(table, colsForSave);
        setTimeout(() => {
          isRestoringRef.current = false;
          table.redraw?.(true);
          captureHScroll();
          refreshFromStart();
        }, 0);
      },
    }),
    [state, closeMenu, getTable, setUserMaskFilter, toast]
  );

  const setMask = useCallback(
    createMaskSetFieldHandler({
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_LIST_COLUMN_VISIBILITY,
      columnsVisible,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyMaintenanceMachineListDataColumnVisibility,
      toast,
    }),
    [columnsVisible, getTable, toast]
  );

  const maskSelectAll = useCallback(
    createMaskSelectAllHandler({
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_LIST_COLUMN_VISIBILITY,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyMaintenanceMachineListDataColumnVisibility,
    }),
    [getTable]
  );

  const maskDeselectAll = useCallback(
    createMaskDeselectAllHandler({
      defaultColumnVisibility: DEFAULT_MAINTENANCE_MACHINE_LIST_COLUMN_VISIBILITY,
      setColumnsVisible,
      getTable,
      applyVisibilityToTable: applyMaintenanceMachineListDataColumnVisibility,
    }),
    [getTable]
  );

  const handleClickOutside = (event) => {
    if (event.target.closest('[data-overflow-trigger]')) return;
    if (overlayRef.current && !overlayRef.current.contains(event.target)) {
      setExpandedKeys({});
      OverflowMenuProvider.setVisible?.(null);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, []);

  const items = [
    {
      key: '0',
      label: t('Common.Actions'),
      icon: 'pi pi-sliders-v',
      template: itemRenderer,
      items: buildMaintenanceMaskAkcieSubItems({
        maskPage: MASK_PAGE,
        getTable,
        setUserMaskFilter,
        cleanSorters,
        captureHScroll,
        refreshFromStart,
        closeMenu,
        openMaskDialog,
        openChosenNotificationPreferences,
        hasApplicableStandardMask,
        confirmApplyStandardLayoutToUserMask,
        itemRenderer,
      }),
    },
  ];

  return (
    <>
      <div className="w-full h-10 flex flex-row bg-[#333]">
        <IntersectionObserverWrapper>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="edit">
            <button className="disabled:opacity-25" disabled={editIcon} onClick={() => editMaintenanceType()} type="button">
              <EditIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="print">
            <button type="button" onClick={() => getTable()?.print?.(false, true)}>
              <PrintIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="search">
            <button
              type="button"
              onClick={
                searchIconColor !== 'active'
                  ? () => setVisibleSearch(true)
                  : () => {
                      setSearchIconColor('');
                      setSearchValue('');
                      submittedSearchValueRef.current = '';
                      captureHScroll();
                      refreshFromStart();
                    }
              }
            >
              {searchIconColor === '' ? (
                <MdSearch fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" title={t('Common.Search')} />
              ) : (
                <MdSearchOff fill="#fff" className="w-[28px] h-[28px]" aria-hidden="true" title={t('Common.CancelSearch')} />
              )}
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="export">
            <button type="button" onClick={() => getTable()?.download?.('xlsx', 'data.xlsx', { sheetName: 'MyData' })}>
              <ExportIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="refresh">
            <button
              type="button"
              onClick={() => {
                captureHScroll();
                refreshFromStart();
              }}
              title={t('Common.Refresh')}
            >
              <RefreshIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="first">
            <button type="button" onClick={() => scrollToFirst()}>
              <BackToFirstIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="back">
            <button type="button" onClick={() => scrollUp()}>
              <BackIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="forward">
            <button type="button" onClick={() => scrollDown()}>
              <ForwardIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="last">
            <button type="button" onClick={() => scrollToLast()}>
              <ForwardToLastIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="favorite">
            <button
              type="button"
              className="disabled:opacity-25"
              disabled={favoriteIcon}
              onClick={() => openFavoriteDialog(t('MaintenanceMachineList.Text.Zoznam_pl_ukonov'), 'MaintenanceMachineList')}
            >
              <FavoriteIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="fast">
            <button type="button" onClick={() => setFastView('MaintenanceMachineList', columnsVisible, columns)}>
              <PriceIcon />
            </button>
          </div>
          <div className="flex flex-col w-10 h-10 px-2 py-1 justify-center" data-targetid="vert">
            <VerticalLineIcon />
          </div>
          {canSetStandardMask ? (
            <div className="flex w-10 h-10 px-2 py-1 justify-center" data-targetid="save-standard-mask">
              <button
                type="button"
                title={t('Common.SaveStandardMask')}
                aria-label={t('Common.SaveStandardMask')}
                onClick={confirmSaveStandardMask}
                className="flex items-center justify-center"
              >
                <i className="pi pi-bookmark text-white text-xl" aria-hidden="true" />
              </button>
            </div>
          ) : null}
          <div className="card flex justify-content-center z-[100]" data-targetid="actions" ref={overlayRef}>
            <PanelMenu model={items} expandedKeys={expandedKeys} onExpandedKeysChange={setExpandedKeys} className="w-full md:w-20rem" />
          </div>
        </IntersectionObserverWrapper>
      </div>
      <div className="px-1 bg-white rounded-md shadow-md">
        <div className="flex flex-col overflow-x-auto no-scrollbar min-h-[calc(100vh-177px)]">
          <div className="overflow-hidden">
            <div className="inline-block w-full py-2">
              <div ref={elementRef}>
                <ReactTabulator
                  onRef={(r) => {
                    ref.current = r.current;
                  }}
                  className="h-[calc(100vh_-_193px)]"
                  columns={columns}
                  events={{
                    rowSelectionChanged: onSelectRow,
                    tableBuilt: () => {
                      const table = getTable();
                      if (!table) return;
                      isRestoringRef.current = true;
                      const effective = getEffectiveMask(MASK_PAGE, state);
                      if (effective?.type === 'user' && effective.userMask) {
                        applyUserMaskRowToTabulator(table, effective.userMask, {
                          setColumnsVisible,
                          clearHeaderFiltersWhenEmpty: true,
                          forceFrozenId: true,
                          applyColumnVisibilityFn: (tbl, jc, setVis) => {
                            const merged = mergeMaintenanceMachineListColumnVisibilityFromSaved(jc);
                            setVis(merged);
                            applyMaintenanceMachineListDataColumnVisibility(tbl, merged);
                          },
                        });
                      } else if (effective?.type === 'standard' && effective.standardMask) {
                        const m = effective.standardMask;
                        const attributes = safeParse(m.attributes, []);
                        const jsonColumns = safeParse(m.columns, {});
                        if (Array.isArray(attributes) && attributes.length > 1) {
                          table.setColumnLayout?.(sanitizeTabulatorLayoutAttributes(attributes, TABULATOR_GROUP_A_FLAT_REMOTE_LAYOUT_SANITIZE_OPTIONS));
                        }
                        if (jsonColumns && typeof jsonColumns === 'object') {
                          const merged = mergeMaintenanceMachineListColumnVisibilityFromSaved(jsonColumns);
                          setColumnsVisible(merged);
                          applyMaintenanceMachineListDataColumnVisibility(table, merged);
                        }
                      }
                      setTimeout(() => {
                        isRestoringRef.current = false;
                      }, 0);
                      tableBuiltRef.current = true;
                      refreshFromStart();
                    },
                    ...getTabulatorPersistLayoutEvents(MASK_PAGE, ref, setUserMaskFilter),
                    headerFilterChanged: () => {
                      if (!isRestoringRef.current) {
                        captureHScroll();
                        refreshFromStart();
                      }
                    },
                    sortChanged: () => {
                      if (!isRestoringRef.current) {
                        captureHScroll();
                        refreshFromStart();
                      }
                    },
                    renderComplete: () => restoreHScroll(),
                  }}
                  options={{
                    layout: 'fitColumns',
                    renderHorizontal: 'virtual',
                    rowHeight: 42,
                    movableColumns: true,
                    headerSortTristate: true,
                    ajaxURL: '/api/maintenance_types/paged',
                    ajaxParams: () => ({}),
                    ajaxRequestFunc: createDedupedAjaxRequest(
                      buildRequestParams,
                      tabulatorGet,
                      inFlightRef,
                      completedCacheRef
                    ),
                    dataLoader: true,
                    dataLoaderLoading: t('Common.LoadingEllipsis'),
                    dataLoaderError: t('Common.LoadFailed'),
                    progressiveLoad: 'scroll',
                    progressiveLoadScrollMargin: 80,
                    paginationSize: pageSizePc,
                    filterMode: 'remote',
                    sortMode: 'remote',
                    pagination: false,
                    ajaxRequesting: () => {
                      if (tableBuiltRef.current && !isRestoringRef.current) captureHScroll();
                      const table = getTable();
                      if (table?.initialized) table.alert?.(t('Common.LoadingEllipsis'));
                      return true;
                    },
                    ajaxResponse: (url, params, response) => {
                      getTable()?.clearAlert?.();
                      return response;
                    },
                    ajaxError: () => {
                      getTable()?.clearAlert?.();
                    },
                    printAsHtml:true,printHeader: () => buildCanonicalPrintHeaderHtml({
                      branding: state?.branding,
                      productFallbackSrc: defaultProductLogoSrc,
                      companySettingsPrint: state?.company_settings_print,
                      moduleSettings: state?.module_settings,
                      title: t('Print.Title.MaintenanceMachineList'),
                      printDateLabel: t('Print.DateLabel'),
                    }),
                    printFooter: () => buildCanonicalPrintFooterHtml({
                      companySettingsPrint: state?.company_settings_print,
                    }),
                    printRowRange: 'active',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog header={t('Common.SearchInList')} visible={visibleSearch} style={{ width: '40vw' }} onHide={() => setVisibleSearch(false)}>
        <div className="flex flex-columns">
          <input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            type="text"
            className="block w-full px-4 py-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
          />
          <button
            type="button"
            onClick={() => searchTable()}
            className="w-16 ml-8 px-4 py-2 tracking-wide text-white transition-colors duration-200 transform bg-zinc-900 rounded-md hover:bg-zinc-700 focus:outline-none focus:bg-zinc-600"
          >
            <SearchIcon />
          </button>
        </div>
      </Dialog>
      <Dialog header={t('Common.SetMask')} visible={maskVisible} style={{ width: '70vw' }} onHide={() => setMaskVisible(false)}>
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 mb-2">{t('MaintenanceTemplateList.MaskHelp')}</p>
          <div className="flex flex-wrap gap-2 mb-2">
            <button type="button" onClick={maskSelectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.SelectAll')}</button>
            <button type="button" onClick={maskDeselectAll} className="px-3 py-1.5 text-sm bg-zinc-200 text-zinc-900 rounded-md hover:bg-zinc-300">{t('Common.DeselectAllExceptId')}</button>
          </div>
          {columns.map((item) => {
            if (!item?.field || MAINTENANCE_MASK_DIALOG_SKIP_FIELDS.has(item.field)) return null;
            return (
              <div key={item.field} className="flex flex-row border border-black rounded-md p-2 m-1">
                <MaintenanceMaskDialogCheckbox field={item.field} inputId={`mml-mask-${item.field}`} title={item.title || item.field} columnsVisible={columnsVisible} onChange={setMask} />
                <label htmlFor={`mml-mask-${item.field}`} className={`ml-2 ${item.field === 'id' ? '' : 'cursor-pointer'}`}>{item.title}</label>
              </div>
            );
          })}
          <button type="button" onClick={saveUserMaskColumns} className="m-1 px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-zinc-700">{t('Common.SaveUpper')}</button>
        </div>
      </Dialog>
    </>
  );
}
